/**
 * Client-side allow-list for the InternalAuth `returnTo` redirect target.
 *
 * The Software Security API already origin-allow-lists `returnTo` when it is
 * written for the forgot-password flow, but the InitialSet flow does not, and this
 * page is the first browser consumer of the field — so re-validate before ever
 * assigning `location.href`. Mirrors the server allow-list: an http/https
 * `localhost` (for local dev) or an `https` `*.universalplant.com` host.
 *
 * Also allows an `https` target on the page's own origin (Bug 35640): an app
 * that hosts this page without a universalplant.com address (the STG employee
 * packet app on azurewebsites.net) can return its employee to itself, and a
 * same-origin redirect cannot leave the app.
 */
export function isSafeReturnTo(
  url: string | null | undefined,
  pageOrigin: string | undefined = globalThis.location?.origin
): boolean {
  if (!url) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  // Reject non-web schemes first — `javascript:`/`data:` etc. can still parse a
  // host (e.g. `javascript://localhost/%0Aalert(1)`), so the host check alone is
  // not enough.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false;
  }

  if (parsed.hostname === 'localhost') {
    return true;
  }

  if (parsed.protocol === 'https:' && parsed.origin === pageOrigin) {
    return true;
  }

  return (
    parsed.protocol === 'https:' &&
    (parsed.hostname === 'universalplant.com' ||
      parsed.hostname.endsWith('.universalplant.com'))
  );
}
